/**
 * TrouveTea — "Trouvé's Tea ☕" expandable explainer.
 *
 * Rendered next to product recommendations (Routine page) and to step
 * techniques on the routine card. When closed: a small pill button
 * inviting curiosity ("Trouvé's Tea ☕"). When open: the explanation
 * text styled as if Trouvé is leaning over to whisper why.
 *
 * Analytics: fires `trouve_tea_opened` once per open (deduplicated per
 * subject id within a session so the same product card doesn't spam
 * events if the user toggles repeatedly).
 *
 * Props:
 *   • text       — the explanation paragraph (required, plain string)
 *   • subjectId  — unique id for analytics dedup (product id, step idx)
 *   • subjectType — `"product" | "step"` (sent as prop on the event)
 *   • testIdSuffix — appended to `trouve-tea-{open|content}-{suffix}`
 *
 * Stealth-mode is honored downstream by `track()` (no-op under stealth).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "../lib/analytics";
import TrouveShareCard from "./TrouveShareCard";

// Track which (subject_type, subject_id) pairs have already emitted
// `trouve_tea_opened` in this page session — prevents double-counting
// when the user toggles the same Tea card open/closed/open.
const _emittedThisSession = new Set();

const TrouveTea = ({ text, subjectId, subjectType, testIdSuffix }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!text) return null;

  const dedupKey = `${subjectType}:${subjectId}`;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !_emittedThisSession.has(dedupKey)) {
      _emittedThisSession.add(dedupKey);
      track("trouve_tea_opened", {
        subject_type: subjectType,
        subject_id: subjectId,
      });
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={`trouve-tea-toggle-${testIdSuffix}`}
        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full transition-all ${
          open
            ? "bg-[#9B6A6C] text-white"
            : "bg-[#9B6A6C]/[0.08] text-[#9B6A6C] hover:bg-[#9B6A6C]/[0.16]"
        }`}
      >
        <span aria-hidden="true">☕</span>
        {open ? t("trouveTea.closeLabel") : t("trouveTea.openLabel")}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div
              className="mt-2 bg-[#9B6A6C]/[0.06] border-l-2 border-[#9B6A6C] rounded-r-xl p-3"
              data-testid={`trouve-tea-content-${testIdSuffix}`}
            >
              <p className="text-[13px] text-[#3D2F33] leading-relaxed italic font-serif">
                &ldquo;{text}&rdquo;
              </p>
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#9B6A6C] font-semibold">
                  — {t("trouveTea.attribution")}
                </p>
                {/* Shareable Trouvé Card — turns this tip into a branded
                    PNG via Web Share API / download. Tracks via the new
                    first-party share_card_* funnel events. */}
                <TrouveShareCard
                  tipText={text}
                  contextId={`trouve-tea-${subjectType}-${subjectId}`}
                  variant="pill"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrouveTea;
